
import Vue from "vue";
import { Component, Prop, Watch } from "vue-property-decorator";
import "./TimetableListItem.scss";
import { Timetable } from "../models/Timetable";
import Passenger from "../../Passenger/models/Passenger";
import Ride from "../models/Ride";
import { Helpers } from "../../../util/Helpers";
import { Guid } from "guid-typescript";
import vuetifyToast from "vuetify-toast";
import { TimetableState } from "@/components/Timetable/models/TimetableState";
import { TimetableStates } from "@/components/Timetable/models/TimetableStates";
import { TimetableEditor } from "@/components/Timetable/components/TimetableEditor.vue";
import { TimetableInfoDialog } from "@/components/Timetable/components/TimetableInfoDialog.vue";
import { RolePermission } from "@/models/Role";
import TimetablePassenger from "@/components/Timetable/models/TimetablePassenger";
import Destination from "@/components/Passenger/models/Destination";
import TransportationPermission from "../../Passenger/models/TransportationPermission";
import moment from "moment";

@Component({
  components: {}
})
export class TimetableListItem extends Vue {
  @Prop({ required: true })
  timetableId: Guid;
  @Prop({ required: false, default: true })
  listView: boolean;
  @Prop({ default: () => [] })
  headers: any[];
  orderInProgress: boolean = false;
  cancelOrderInProgress: boolean = false;

  s: TimetableStates = new TimetableStates();

  get timetable(): Timetable {
    return this.$store.state.timetable.timetables.find(
      t => t.id === this.timetableId
    );
  }
  get timetableIsAccepted() {
    return this.timetable.isAccepted;
  }
  get timetableIsOrdered() {
    return this.timetable.isOrdered;
  }
  get timetableIsLocked() {
    return this.timetable.isLocked;
  }
  get timetableIsOrderable() {
    return (
      this.timetable.isOrderable &&
      this.timetableHasRides
    );
  }
  get timetableHasRides() {
    return this.timetableRides.length > 0;
  }
  get timetableOrderIsCancelable() {
    return this.timetable.isOrderIsCancelable;
  }
  get timetableHasExpired() {
    return this.timetable.hasExpired;
  }
  get timetableStartDateTooClose() {
    return !this.timetable.hasEnoughTimeBeforeStart;
  }
  get timetableStartDatePassed() {
    return this.timetable.hasStartDatePassed;
  }
  get timetableDaysUntilStart() {
    return this.timetable.daysUntilStart;
  }
  get timetableDaysUntilEnd() {
    return this.timetable.daysUntilEnd;
  }
  get timetableCurrentStateColor() {
    return this.timetable.currentStateColor;
  }
  get timetableCurrentStateAlertClass() {
    return this.timetable.currentStateAlertClass;
  }

  get timetableIsSaved() {
    // TODO: Implement
    // Saved means if there's a order for it and that there's no changes after the previous order are
    return true;
  }
  get timetableRides(): Ride[] {
    return this.$store.state.ride.rides.filter(
      (ride: Ride) => ride.timetableId === this.timetable.id
    );
  }

  get timetablePassengerIds(): number[] {
    // Autofix bug where cached passenger list is saved as empty
    if (
      !this.timetable.timetablePassengers ||
      this.timetable.timetablePassengers.length === 0
    ) {
      if (this.timetableRides.length > 0) {
        const passengerIds: number[] = this.timetableRides.map(
          ride => ride.passengerId
        );
        const uniquePassengerIds: number[] = [...new Set(passengerIds)];
        this.timetable.timetablePassengers = uniquePassengerIds.map(
          pid => new TimetablePassenger(this.timetableId, pid)
        );
      }
    }

    return this.timetable.timetablePassengers
      .map(tp => tp.passengerId);
  }

  set timetablePassengerIds(value) {
    console.log(
      "WARN:",
      "no-op!",
      "TimetableListItem",
      "set timetablePassengerIds()",
      "value:",
      value
    );
  }

  get timetablePassengersCount(): number {
    return this.timetablePassengerIds.length;
  }

  get destinations(): Destination[] {
    return this.$store.state.destination.destinations;
  }

  get selectedDestination(): Destination {
    const dest = this.destinations.find(d => d.id === this.routeDestinationId);
    return dest;
  }

  get isTransportationPermissionEnabled(): boolean {
    return (
      this.me.hasPermissions(this.routeDestinationId, [
        RolePermission.ReadTransportationPermissions
      ]) ||
      this.me.hasPermissions(this.routeDestinationId, [
        RolePermission.CreateTransportationPermissions
      ])
    );
  }

  get timetablePassengersIdsWithRides(): number[] {
    const passengerIds: number[] = this.timetable.timetablePassengers
            .map(tp => tp.passengerId)

    const passengerIdsWithRides: number[] = [];
    passengerIds.forEach(pid => {
      if (this.timetableRides.some((r: Ride) => r.passengerId === pid)) {
        passengerIdsWithRides.push(pid);
      }
    });
    return passengerIdsWithRides;
  }

  get timetablePassengerIdsWithRidesCount(): number {
    const passengerIds: number[] = this.timetable.timetablePassengers
            .map(tp => tp.passengerId);

    const count: number = passengerIds.filter(pid =>
      this.timetableRides.some((r: Ride) => r.passengerId === pid)
    ).length;
    // Cache count for sorting
    this.timetable.passengerWithRidesCount = count;
    return count;
  }

  get routeDestinationId(): number {
    return parseInt(this.$store.state.route.params.destinationId, 10);
  }

  get me() {
    return this.$store.state.user.me;
  }

  get userCanEditTimetables(): boolean {
    return this.me
      ? this.me.hasPermissions(this.routeDestinationId, [
          RolePermission.UpdateTimetables
        ])
      : false;
  }

  timetableHasPassenger(t, passenger) {
    return t.timetablePassengers.some(tp => tp.passengerId === passenger.id);
  }

  async saveTimetable(timetable: Timetable) {
    await this.$store.dispatch("timetable/updateTimetable", {
      params: { id: timetable.id },
      data: Timetable.toApi(timetable)
    });
  }

  get helpers() {
    return Helpers;
  }

  activate() {
    this.$store.dispatch("app/setLastSelectedTimetableId", this.timetable.id);
  }

  onTimetableEditClick() {
    this.$root.$emit(
      TimetableEditor.EVENT_TIMETABLE_EDITOR_OPEN,
      this.timetable
    );
  }

  onTimetableInfoClick() {
    this.$root.$emit(
      TimetableInfoDialog.EVENT_TIMETABLE_INFO_DIALOG_OPEN,
      this.timetable
    );
  }

  async onCancelOrderTimetableClick() {
    // TODO: Send the order cancellation, don't just update the state, let the api update the state
    // TODO: Do we need a state OrderCancelled?
    this.cancelOrderInProgress = true;
    try {
      const apiTimetable = Timetable.toApi(this.timetable);
      apiTimetable.state = TimetableStates.New.id;
      await this.saveTimetable(apiTimetable);
      vuetifyToast.success(
        this.$i18n.translate(
          "timetable.order.canceled.notification",
          "Order canceled."
        )
      );
    } catch (err) {
      vuetifyToast.error(
        this.$i18n.translate(
          "timetable.order.canceled.error",
          "Error when canceling order: {errorMessage}",
          { errorMessage: (err as any).message }
        )
      );
    }
    this.cancelOrderInProgress = false;
  }
  async onOrderTimetableClick() {
    this.orderInProgress = true;
    try {
      const apiTimetable = Timetable.toApi(this.timetable);
      apiTimetable.state = TimetableStates.Ordered.id;
      await this.saveTimetable(apiTimetable);
      vuetifyToast.success(
        this.$i18n.translate("timetable.order.sent.notification", "Order sent.")
      );
    } catch (err) {
      vuetifyToast.error(
        this.$i18n.translate(
          "timetable.order.sent.error",
          "Error when sending order: {errorMessage}",
          { errorMessage: (err as any).message }
        )
      );
    }

    this.orderInProgress = false;
  }
  onListItemClick(e: Event) {
    if (!this.listView) {
      this.onViewTimetableClick(e);
    }
  }
  onViewTimetableClick(e: Event) {
    this.activate();
    setTimeout(() => {
      this.$router.push({
        name: "Timetable",
        params: {
          timetableId: this.timetable.id.toString()
        }
      });
    }, 300);
  }
  onAddPeopleClick() {
    this.activate();
    setTimeout(() => {
      this.$router.push({
        name: "Timetable",
        params: {
          timetableId: this.timetable.id.toString()
        },
        query: { addpeople: "true" }
      });
    }, 500);
  }
}

export default TimetableListItem;
